
import { throwError as observableThrowError, Observable, Subscriber, throwError, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { OrderService } from '../../order/services/order.service';
import { Order } from '../models/order';
import { Product } from '../models/product';
import { OrderTypeEnum } from '../models/order-type.enum';
import { ProductsStateService } from './products-state.service';
import { OrderInfo } from '../models/order-info';
import { environment } from '../../../../environments/environment';
import { OrderInMemoryService } from './order-in-memory.service';
import { LoggerService } from '../../../services/logger/logger.service';
import { DiscountStateService } from '../../payment/services/discount-state.service';
import { PaymentGiftStateService } from '../../payment/services/payment-gift-state.service';
import { BookingSlot } from '../../booking/models/booking-slot';
import { PriceListStateService } from '../../payment/services/pricelist-state.service';
import { ShopSettingsService } from '../../general/services/shop-settings.service';
import { OrderLineType } from '../models/order-line-type';
import { PartnerStateService } from '../../partner/services/partner-state.service';
import { VuConnectionHttpService } from '../../vu/services/vu-connection-http.service';
import { ProductsService } from './products.service';
import { ProductStockError } from '../models/product-stock-error';


@Injectable()
export class OrderStateService {

  private _order: Order;
  // private _serviceFeeProduct: Product;
  private _orderKey = 'paymentOrder';
  private _paymentProviderKey = 'paymentProvider';

  private readonly _orderSessionKey = 'currentOrder';

  guestOrderKey: string;

  constructor(
    private orderService: OrderService,
    private productsService: ProductsService,
    private productsStateService: ProductsStateService,
    private logger: LoggerService,
    private discountStateService: DiscountStateService,
    private paymentGiftStateService: PaymentGiftStateService,
    private priceListStateService: PriceListStateService,
    private shopSettingsService: ShopSettingsService,
    private partnerStateService: PartnerStateService,
    private vuConnectionHttpService: VuConnectionHttpService,
  ) {
  }

  get order(): Order {
    return this._order;
  }

  get orderId(): number {
    return this._order && this._order.id ? this._order.id : 0;
  }

  get sessionOrderId(): number {
    const value = parseInt(sessionStorage.getItem(this._orderKey), 10);
    return value ? value : 0;
  }

  set sessionOrderId(value: number) {
    sessionStorage.setItem(this._orderKey, value + '');
  }

  get sessionPaymentProviderId(): number {
    const value = parseInt(sessionStorage.getItem(this._paymentProviderKey), 10);
    return value ? value : -1;
  }

  set sessionPaymentProviderId(value: number) {
    sessionStorage.setItem(this._paymentProviderKey, value + '');
  }

  reset(): void {
    this.logger.info('OrderStateService: reset.');
    // this._serviceFeeProduct = null;
    this._order = null;
  }

  initialize(orderType: OrderTypeEnum): Observable<boolean> {

    return Observable.create(
      (observable: Subscriber<boolean>) => {

        this.logger.info('OrderStateService: initialize.');
        const defaultProductPricelist = this.priceListStateService.defaultPricelist;
        this._order = new Order(orderType, defaultProductPricelist);

        observable.next(true);
        observable.complete();
        // if (this._serviceFeeProduct) {
        //   this._addServiceFeeProduct(this._serviceFeeProduct);
        //   observable.next(true);
        //   this.logger.info('OrderStateService: initialize done(1).');
        // } else {
        //   this.productsStateService.serviceFeeProduct.subscribe(
        //     (serviceFeeProduct: Product) => {
        //       this._serviceFeeProduct = serviceFeeProduct;
        //       this._addServiceFeeProduct(this._serviceFeeProduct);
        //       observable.next(true);
        //       this.logger.info('OrderStateService: read serviceFeeProduct done.');
        //       this.logger.info('OrderStateService: initialize done(2).');
        //     }
        //   );
        // }

      });
  }

  // private _addServiceFeeProduct(serviceFeeProduct: Product) {
  //   if (!serviceFeeProduct) {
  //     return;
  //   }
  //   this._order.addOrderLine(
  //     OrderLineType.Fee,
  //     serviceFeeProduct.name,
  //     serviceFeeProduct.id,
  //     serviceFeeProduct.price,
  //     1,
  //     true
  //   );
  // }

  loadOrder(orderId: number, orderKey: string = null): Observable<boolean> {
    return of(true).pipe(
      tap(_ => this.logger.info(`OrderStateService: loadOrder orderId: ${orderId}.`)),
      switchMap(_ => {
        if (orderKey) {
          return this.orderService.getGuestOrder(orderId, orderKey);
        }
        return this.orderService.getOrder(orderId);
      }),
      map(order => {
        this.reset();
        this._order = order;
        this.paymentGiftStateService.loadRedeemedGiftCards(order.redeemedGiftAmounts);
        this.discountStateService.loadCoupons(order.coupons);
        this.logger.info(`OrderStateService: loadOrder done orderId: ${this._order.id}, state: ${this._order.state}.`);
        return true;
      }),
      catchError(error => {
        this.logger.info(`OrderStateService: loadOrder FAILED orderId: ${orderId}, error: ${error}`);
        return throwError(error);
      }),
    );
  }

  createOrder(): Observable<Order> {
    return of(true).pipe(
      tap(_ => this.logger.info(`OrderStateService: createOrder.`)),
      switchMap(_ => {
        const stockProductRequestQuantity = this.order.getStockProductRequestQuantity();
        if (!stockProductRequestQuantity || stockProductRequestQuantity.length == 0) {
          return of(true);
        }
        return this.productsService.checkIsAvailableProductOnStock(stockProductRequestQuantity).pipe(
          map(result => {
            let noProductsStockInformation = [];
            for (const productStockInformation of result) {
              if (!productStockInformation.available) {
                this.order.setAvailableProductQuantity(productStockInformation.productId, productStockInformation.availableQuantity, productStockInformation.locationId);
                noProductsStockInformation.push(productStockInformation);
              }
            }
            if (noProductsStockInformation.length > 0) {
              throw new ProductStockError('No product on stock', noProductsStockInformation);
            }
            return true;
          }),
        );
      }),
      tap(_ => {
        if (this.shopSettingsService.shopSettings.partnerCodeAuthorization && this.partnerStateService.loggedInPartner !== null) {
          const orderLines = this.order.orderLines;
          for (const orderLine of orderLines) {
            if (orderLine.orderLineType === OrderLineType.GiftCard) {
              orderLine.rfid_card_ean13 = this.partnerStateService.loggedInPartner.barcode;
            }
          }
        }
      }),
      tap(_ => this.order.updateMultiOrderState()),
      switchMap(_ => {
        if (this.shopSettingsService.isPosOrderMode) {
          return this.vuConnectionHttpService.createOrder(this.order);
        }

        if (this.partnerStateService.loggedInPartner == null) {
          return this.orderService.createGuestOrder(this.order);
        }

        return this.orderService.createOrder(this.order);
      }),
      tap(order => {
        this._order = order;
        this.logger.info(`OrderStateService: createOrder done orderId: ${this._order.id}, state: ${this._order.state}.`);
      }),
      catchError(error => {
        this.logger.info(`OrderStateService: createOrder FAILED error: ${error}.`);
        return throwError(error);
      }),
    );
  }

  changeOrderTimeSlot(order: Order, bookingSlot: BookingSlot): Observable<any> {
    return of(true).pipe(
      map(_ => {
        this.logger.info(`OrderStateService: changeOrderTimeSlot.`);
        return environment.globalSettings.getIsoDateStrByDate(bookingSlot.bookingDate);
      }),
      switchMap(bookingDate => this.orderService.changeOrderTimeSlot(order.id, bookingDate, bookingSlot.id)),
      tap(_ => {
        this.logger.info(`OrderStateService: changeOrderTimeSlot done orderId: ${this._order.id}.`);
      }),
      catchError(error => {
        this.logger.info(`OrderStateService: createOrder FAILED error: ${error}.`);
        return throwError(error);
      }),
    );
  }

  cancelOrder(orderId: number): Observable<any> {
    return this.orderService.cancelOrder(orderId);
  }

  saleToInvoice(orderId: number): Observable<boolean> {
    return this.orderService.saleToInvoice(orderId);
  }

  get allOrders(): Observable<OrderInfo[]> {
    return this.orderService.loggedInPartnerOrders;
  }

  inMemoryOrderPaymentComplete(order: Order): void {
    if (environment.production) {
      return;
    }

    const orderInMemoryService = this.orderService as OrderInMemoryService;
    if (orderInMemoryService) {
      orderInMemoryService.orderPaymentComplete(order);
    }
  }

  getAutomaticAdditionalProductByIds(automaticAdditionalProductsIds): Observable<Product[]> {
    if (automaticAdditionalProductsIds) {
      return this.productsStateService.getAutomaticAdditionalProducts(automaticAdditionalProductsIds);
    } else {
      return of(null);
    }
  }

  refundOrder(orderId: number): Observable<any> {
    return this.orderService.refundOrder(orderId);
  }

  orderSaveToLocalStorage(): void {
    localStorage.setItem(this._orderSessionKey, JSON.stringify(this.order.toJSON()));
  }

  orderLoadFromLocalStorage(): void {
    const orderJson = localStorage.getItem(this._orderSessionKey);
    const orderData = JSON.parse(orderJson);
    this._order = Order.createFromAny(orderData);
  }

  orderRemoveFromLocalStore(): void {
    localStorage.removeItem(this._orderSessionKey);
  }

  get isNeedReloadOrder(): boolean {
    if (this._order) {
      return false;
    }
    return this.orderExistInLocalStore();
  }

  orderExistInLocalStore(): boolean {
    return localStorage.getItem(this._orderSessionKey) != null;
  }

  sendGuestOrderToEmail(email: string): Observable<boolean> {
    return of(false).pipe(
      switchMap(
        _ => {
          if (!this.order || !this.order.guestOrderKey || !email) {
            return of(false);
          }
          return this.orderService.sendGuestOrderToEmail(this.order.id, this.order.guestOrderKey, email);
        }
      )
    );
  }

}
